/**
 * Página de Comunicação - Área Administrativa
 * 
 * Gerenciamento de:
 * - Configurações de Email
 * - Campanhas de Email em Massa
 * - Notificações do Sistema (Sino)
 */

import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../services/staticDataConfig';

// ============================================
// TIPOS
// ============================================

interface ConfiguracaoEmail {
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_from_email: string;
  smtp_from_name: string;
  status: 'configurado' | 'nao_configurado' | 'erro';
  ultimo_teste?: string;
  resultado_ultimo_teste?: string;
}

interface SegmentacaoUsuarios {
  tipo_empresa?: string[];
  cargo?: string[];
  ativo?: boolean;
  empresas_ids?: number[];
  usuarios_ids?: number[];
  todos?: boolean;
}

interface CampanhaEmail {
  id?: number;
  titulo: string;
  assunto: string;
  corpo_html: string;
  corpo_texto?: string;
  tipo: 'marketing' | 'manutencao' | 'aviso' | 'novidade';
  segmentacao: SegmentacaoUsuarios;
  status: 'rascunho' | 'agendada' | 'enviando' | 'concluida' | 'erro';
  data_agendamento?: string;
  total_destinatarios?: number;
  emails_enviados?: number;
  emails_erro?: number;
  created_at?: string;
}

interface NotificacaoSistema {
  id?: number;
  titulo: string;
  mensagem: string;
  tipo: 'info' | 'warning' | 'success' | 'danger';
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente';
  icone?: string;
  link?: string;
  segmentacao: SegmentacaoUsuarios;
  frequencia: 'unica' | 'diaria' | 'semanal';
  data_inicio: string;
  data_fim?: string;
  ativa: boolean;
  created_at?: string;
}

interface Estatisticas {
  campanhas_total: number;
  campanhas_enviadas: number;
  emails_enviados_total: number;
  notificacoes_ativas: number;
  usuarios_alcancados: number;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function AdminComunicacao() {
  const { token } = useAdminAuth();
  const [abaAtiva, setAbaAtiva] = useState<'config' | 'campanhas' | 'notificacoes'>('config');
  const [loading, setLoading] = useState(true);
  
  // Estados de configuração
  const [configEmail, setConfigEmail] = useState<ConfiguracaoEmail | null>(null);
  const [emailTeste, setEmailTeste] = useState('');
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState<{sucesso: boolean; mensagem: string} | null>(null);
  
  // Estados de campanhas
  const [campanhas, setCampanhas] = useState<CampanhaEmail[]>([]);
  const [campanhaEditando, setCampanhaEditando] = useState<CampanhaEmail | null>(null);
  const [modalCampanha, setModalCampanha] = useState(false);
  
  // Estados de notificações
  const [notificacoes, setNotificacoes] = useState<NotificacaoSistema[]>([]);
  const [notificacaoEditando, setNotificacaoEditando] = useState<NotificacaoSistema | null>(null);
  const [modalNotificacao, setModalNotificacao] = useState(false);
  
  // Estados auxiliares
  const [tiposEmpresa, setTiposEmpresa] = useState<string[]>([]);
  const [cargos, setCargos] = useState<string[]>([]);
  const [empresas, setEmpresas] = useState<Array<{ id: number; nome: string; tipo: string }>>([]);
  const [usuarios, setUsuarios] = useState<Array<{ id: number; nome: string; email: string; empresa: string }>>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [quantidadeDestinatarios, setQuantidadeDestinatarios] = useState<number>(0);
  const [modoSegmentacao, setModoSegmentacao] = useState<'filtros' | 'empresas' | 'usuarios'>('filtros');

  // ============================================
  // FUNÇÕES DE API
  // ============================================

  const carregarConfigEmail = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/email/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfigEmail(data.dados);
      }
    } catch (error) {
      console.error('Erro ao carregar config email:', error);
    }
  }, [token]);

  const carregarCampanhas = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/campanhas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampanhas(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    }
  }, [token]);

  const carregarNotificacoes = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/notificacoes-sistema`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificacoes(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    }
  }, [token]);

  const carregarEstatisticas = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/estatisticas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEstatisticas(data.dados);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, [token]);

  const carregarTiposEmpresa = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/tipos-empresa`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTiposEmpresa(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar tipos empresa:', error);
    }
  }, [token]);

  const carregarCargos = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/cargos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCargos(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    }
  }, [token]);

  const carregarEmpresas = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/empresas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmpresas(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  }, [token]);

  const carregarUsuarios = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/usuarios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsuarios(data.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  }, [token]);

  const contarDestinatarios = async (segmentacao: SegmentacaoUsuarios) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/destinatarios/contar`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(segmentacao)
      });
      if (response.ok) {
        const data = await response.json();
        setQuantidadeDestinatarios(data.dados.quantidade);
      }
    } catch (error) {
      console.error('Erro ao contar destinatários:', error);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      await Promise.all([
        carregarConfigEmail(),
        carregarCampanhas(),
        carregarNotificacoes(),
        carregarEstatisticas(),
        carregarTiposEmpresa(),
        carregarCargos(),
        carregarEmpresas(),
        carregarUsuarios()
      ]);
      setLoading(false);
    };
    carregar();
  }, [carregarConfigEmail, carregarCampanhas, carregarNotificacoes, carregarEstatisticas, carregarTiposEmpresa, carregarCargos, carregarEmpresas, carregarUsuarios]);

  // ============================================
  // HANDLERS
  // ============================================

  const testarEmail = async () => {
    if (!emailTeste) {
      alert('Digite um email para teste');
      return;
    }

    setTestando(true);
    setResultadoTeste(null);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/email/testar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailTeste })
      });
      const data = await response.json();
      setResultadoTeste({ sucesso: data.sucesso, mensagem: data.mensagem });
      carregarConfigEmail();
    } catch (error) {
      setResultadoTeste({ sucesso: false, mensagem: 'Erro ao testar envio' });
    } finally {
      setTestando(false);
    }
  };

  const salvarCampanha = async () => {
    if (!campanhaEditando) return;

    try {
      const method = campanhaEditando.id ? 'PUT' : 'POST';
      const url = campanhaEditando.id 
        ? `${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/campanhas/${campanhaEditando.id}`
        : `${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/campanhas`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(campanhaEditando)
      });

      if (response.ok) {
        setModalCampanha(false);
        setCampanhaEditando(null);
        carregarCampanhas();
        carregarEstatisticas();
        alert('Campanha salva com sucesso!');
      } else {
        alert('Erro ao salvar campanha');
      }
    } catch (error) {
      console.error('Erro ao salvar campanha:', error);
      alert('Erro ao salvar campanha');
    }
  };

  const enviarCampanha = async (id: number) => {
    if (!confirm('Deseja realmente enviar esta campanha? Esta ação não pode ser desfeita.')) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/campanhas/${id}/enviar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      alert(data.mensagem);
      carregarCampanhas();
      carregarEstatisticas();
    } catch (error) {
      console.error('Erro ao enviar campanha:', error);
      alert('Erro ao enviar campanha');
    }
  };

  const excluirCampanha = async (id: number) => {
    if (!confirm('Deseja excluir esta campanha?')) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/campanhas/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        carregarCampanhas();
        carregarEstatisticas();
      }
    } catch (error) {
      console.error('Erro ao excluir campanha:', error);
    }
  };

  const salvarNotificacao = async () => {
    if (!notificacaoEditando) return;

    try {
      const method = notificacaoEditando.id ? 'PUT' : 'POST';
      const url = notificacaoEditando.id 
        ? `${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/notificacoes-sistema/${notificacaoEditando.id}`
        : `${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/notificacoes-sistema`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificacaoEditando)
      });

      if (response.ok) {
        setModalNotificacao(false);
        setNotificacaoEditando(null);
        carregarNotificacoes();
        carregarEstatisticas();
        alert('Notificação salva com sucesso!');
      } else {
        alert('Erro ao salvar notificação');
      }
    } catch (error) {
      console.error('Erro ao salvar notificação:', error);
      alert('Erro ao salvar notificação');
    }
  };

  const excluirNotificacao = async (id: number) => {
    if (!confirm('Deseja excluir esta notificação?')) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/comunicacao/notificacoes-sistema/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        carregarNotificacoes();
        carregarEstatisticas();
      }
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
    }
  };

  const novaCampanha = () => {
    setCampanhaEditando({
      titulo: '',
      assunto: '',
      corpo_html: '',
      corpo_texto: '',
      tipo: 'aviso',
      segmentacao: { todos: true },
      status: 'rascunho'
    });
    setQuantidadeDestinatarios(0);
    setModalCampanha(true);
  };

  const novaNotificacao = () => {
    setNotificacaoEditando({
      titulo: '',
      mensagem: '',
      tipo: 'info',
      prioridade: 'normal',
      icone: 'fa-bell',
      segmentacao: { todos: true },
      frequencia: 'unica',
      data_inicio: new Date().toISOString().slice(0, 16),
      ativa: true
    });
    setQuantidadeDestinatarios(0);
    setModalNotificacao(true);
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="fas fa-bullhorn me-2"></i>
          Central de Comunicação
        </h2>
      </div>

      {/* Cards de Estatísticas */}
      {estatisticas && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{estatisticas.campanhas_total}</h4>
                    <small>Campanhas Criadas</small>
                  </div>
                  <i className="fas fa-envelope fa-2x opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{estatisticas.emails_enviados_total}</h4>
                    <small>Emails Enviados</small>
                  </div>
                  <i className="fas fa-paper-plane fa-2x opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{estatisticas.notificacoes_ativas}</h4>
                    <small>Notificações Ativas</small>
                  </div>
                  <i className="fas fa-bell fa-2x opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-dark">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{estatisticas.usuarios_alcancados}</h4>
                    <small>Usuários Alcançados</small>
                  </div>
                  <i className="fas fa-users fa-2x opacity-50"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${abaAtiva === 'config' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('config')}
          >
            <i className="fas fa-cog me-2"></i>
            Configurações de Email
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${abaAtiva === 'campanhas' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('campanhas')}
          >
            <i className="fas fa-paper-plane me-2"></i>
            Campanhas de Email
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${abaAtiva === 'notificacoes' ? 'active' : ''}`}
            onClick={() => setAbaAtiva('notificacoes')}
          >
            <i className="fas fa-bell me-2"></i>
            Notificações do Sistema
          </button>
        </li>
      </ul>

      {/* Conteúdo das Abas */}
      {abaAtiva === 'config' && (
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              <i className="fas fa-server me-2"></i>
              Configurações do Servidor de Email
            </h5>
          </div>
          <div className="card-body">
            {configEmail && (
              <>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="alert alert-info">
                      <h6><i className="fas fa-info-circle me-2"></i>Provedor Atual</h6>
                      <p className="mb-0">
                        <strong>AWS SES (Simple Email Service)</strong><br/>
                        Região: sa-east-1 (São Paulo)
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className={`alert ${configEmail.status === 'configurado' ? 'alert-success' : 'alert-warning'}`}>
                      <h6>
                        <i className={`fas ${configEmail.status === 'configurado' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                        Status
                      </h6>
                      <p className="mb-0">
                        {configEmail.status === 'configurado' ? 'Servidor configurado e pronto' : 'Credenciais não configuradas'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="table-responsive mb-4">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: '200px' }}>Host SMTP</th>
                        <td>{configEmail.smtp_host}</td>
                      </tr>
                      <tr>
                        <th>Porta</th>
                        <td>{configEmail.smtp_port}</td>
                      </tr>
                      <tr>
                        <th>Usuário</th>
                        <td>{configEmail.smtp_user}</td>
                      </tr>
                      <tr>
                        <th>Email Remetente</th>
                        <td>{configEmail.smtp_from_email}</td>
                      </tr>
                      <tr>
                        <th>Nome Remetente</th>
                        <td>{configEmail.smtp_from_name}</td>
                      </tr>
                      {configEmail.ultimo_teste && (
                        <>
                          <tr>
                            <th>Último Teste</th>
                            <td>{new Date(configEmail.ultimo_teste).toLocaleString('pt-BR')}</td>
                          </tr>
                          <tr>
                            <th>Resultado</th>
                            <td>
                              <span className={`badge ${configEmail.resultado_ultimo_teste?.includes('sucesso') ? 'bg-success' : 'bg-danger'}`}>
                                {configEmail.resultado_ultimo_teste}
                              </span>
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="card bg-light">
                  <div className="card-body">
                    <h6><i className="fas fa-vial me-2"></i>Testar Envio de Email</h6>
                    <div className="row">
                      <div className="col-md-8">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Digite o email para teste..."
                          value={emailTeste}
                          onChange={(e) => setEmailTeste(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4">
                        <button 
                          className="btn btn-primary w-100"
                          onClick={testarEmail}
                          disabled={testando}
                        >
                          {testando ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Enviando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane me-2"></i>
                              Enviar Teste
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    {resultadoTeste && (
                      <div className={`alert ${resultadoTeste.sucesso ? 'alert-success' : 'alert-danger'} mt-3 mb-0`}>
                        <i className={`fas ${resultadoTeste.sucesso ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
                        {resultadoTeste.mensagem}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {abaAtiva === 'campanhas' && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="fas fa-paper-plane me-2"></i>
              Campanhas de Email
            </h5>
            <button className="btn btn-success" onClick={novaCampanha}>
              <i className="fas fa-plus me-2"></i>
              Nova Campanha
            </button>
          </div>
          <div className="card-body">
            {campanhas.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-3x mb-3"></i>
                <p>Nenhuma campanha criada</p>
                <button className="btn btn-primary" onClick={novaCampanha}>
                  Criar primeira campanha
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th>Enviados</th>
                      <th>Data</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campanhas.map(campanha => (
                      <tr key={campanha.id}>
                        <td>
                          <strong>{campanha.titulo}</strong>
                          <br/>
                          <small className="text-muted">{campanha.assunto}</small>
                        </td>
                        <td>
                          <span className={`badge ${
                            campanha.tipo === 'marketing' ? 'bg-primary' :
                            campanha.tipo === 'manutencao' ? 'bg-warning' :
                            campanha.tipo === 'novidade' ? 'bg-info' : 'bg-secondary'
                          }`}>
                            {campanha.tipo}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            campanha.status === 'concluida' ? 'bg-success' :
                            campanha.status === 'enviando' ? 'bg-primary' :
                            campanha.status === 'erro' ? 'bg-danger' :
                            campanha.status === 'agendada' ? 'bg-info' : 'bg-secondary'
                          }`}>
                            {campanha.status}
                          </span>
                        </td>
                        <td>
                          {campanha.emails_enviados || 0} / {campanha.total_destinatarios || 0}
                          {(campanha.emails_erro || 0) > 0 && (
                            <span className="text-danger ms-2">
                              ({campanha.emails_erro} erros)
                            </span>
                          )}
                        </td>
                        <td>
                          {campanha.created_at && new Date(campanha.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            {campanha.status === 'rascunho' && (
                              <>
                                <button 
                                  className="btn btn-outline-primary"
                                  onClick={() => { setCampanhaEditando(campanha); setModalCampanha(true); }}
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button 
                                  className="btn btn-outline-success"
                                  onClick={() => enviarCampanha(campanha.id!)}
                                  title="Enviar"
                                >
                                  <i className="fas fa-paper-plane"></i>
                                </button>
                                <button 
                                  className="btn btn-outline-danger"
                                  onClick={() => excluirCampanha(campanha.id!)}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </>
                            )}
                            {campanha.status !== 'rascunho' && (
                              <button 
                                className="btn btn-outline-info"
                                onClick={() => { setCampanhaEditando(campanha); setModalCampanha(true); }}
                                title="Visualizar"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {abaAtiva === 'notificacoes' && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="fas fa-bell me-2"></i>
              Notificações do Sistema
            </h5>
            <button className="btn btn-success" onClick={novaNotificacao}>
              <i className="fas fa-plus me-2"></i>
              Nova Notificação
            </button>
          </div>
          <div className="card-body">
            {notificacoes.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-bell-slash fa-3x mb-3"></i>
                <p>Nenhuma notificação criada</p>
                <button className="btn btn-primary" onClick={novaNotificacao}>
                  Criar primeira notificação
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Tipo</th>
                      <th>Prioridade</th>
                      <th>Frequência</th>
                      <th>Status</th>
                      <th>Período</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notificacoes.map(notif => (
                      <tr key={notif.id}>
                        <td>
                          <i className={`fas ${notif.icone || 'fa-bell'} me-2`}></i>
                          <strong>{notif.titulo}</strong>
                          <br/>
                          <small className="text-muted">{notif.mensagem.substring(0, 50)}...</small>
                        </td>
                        <td>
                          <span className={`badge bg-${notif.tipo}`}>
                            {notif.tipo}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            notif.prioridade === 'urgente' ? 'bg-danger' :
                            notif.prioridade === 'alta' ? 'bg-warning' :
                            notif.prioridade === 'normal' ? 'bg-info' : 'bg-secondary'
                          }`}>
                            {notif.prioridade}
                          </span>
                        </td>
                        <td>{notif.frequencia}</td>
                        <td>
                          <span className={`badge ${notif.ativa ? 'bg-success' : 'bg-secondary'}`}>
                            {notif.ativa ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                        <td>
                          <small>
                            {new Date(notif.data_inicio).toLocaleDateString('pt-BR')}
                            {notif.data_fim && ` - ${new Date(notif.data_fim).toLocaleDateString('pt-BR')}`}
                          </small>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => { setNotificacaoEditando(notif); setModalNotificacao(true); }}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className="btn btn-outline-danger"
                              onClick={() => excluirNotificacao(notif.id!)}
                              title="Excluir"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Campanha */}
      {modalCampanha && campanhaEditando && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-envelope me-2"></i>
                  {campanhaEditando.id ? 'Editar Campanha' : 'Nova Campanha'}
                </h5>
                <button className="btn-close" onClick={() => setModalCampanha(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-8 mb-3">
                    <label className="form-label">Título Interno *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={campanhaEditando.titulo}
                      onChange={(e) => setCampanhaEditando({...campanhaEditando, titulo: e.target.value})}
                      placeholder="Ex: Campanha de Boas-vindas Janeiro"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Tipo</label>
                    <select
                      className="form-select"
                      value={campanhaEditando.tipo}
                      onChange={(e) => setCampanhaEditando({...campanhaEditando, tipo: e.target.value as any})}
                    >
                      <option value="marketing">Marketing</option>
                      <option value="manutencao">Manutenção</option>
                      <option value="aviso">Aviso</option>
                      <option value="novidade">Novidade</option>
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Assunto do Email *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={campanhaEditando.assunto}
                    onChange={(e) => setCampanhaEditando({...campanhaEditando, assunto: e.target.value})}
                    placeholder="Assunto que aparecerá no email"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Corpo do Email (HTML) *</label>
                  <textarea
                    className="form-control"
                    rows={8}
                    value={campanhaEditando.corpo_html}
                    onChange={(e) => setCampanhaEditando({...campanhaEditando, corpo_html: e.target.value})}
                    placeholder="<h1>Olá {{nome}}</h1><p>Conteúdo do email...</p>"
                  />
                  <small className="text-muted">
                    Variáveis disponíveis: {'{{nome}}'}, {'{{email}}'}, {'{{empresa}}'}
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Corpo do Email (Texto Plano)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={campanhaEditando.corpo_texto || ''}
                    onChange={(e) => setCampanhaEditando({...campanhaEditando, corpo_texto: e.target.value})}
                    placeholder="Versão em texto plano (opcional)"
                  />
                </div>

                <hr />

                <h6><i className="fas fa-users me-2"></i>Segmentação de Destinatários</h6>
                
                {/* Abas de modo de segmentação */}
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${modoSegmentacao === 'filtros' ? 'active' : ''}`}
                      onClick={() => {
                        setModoSegmentacao('filtros');
                        setCampanhaEditando({...campanhaEditando, segmentacao: { todos: true }});
                      }}
                    >
                      <i className="fas fa-filter me-1"></i> Por Filtros
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${modoSegmentacao === 'empresas' ? 'active' : ''}`}
                      onClick={() => {
                        setModoSegmentacao('empresas');
                        setCampanhaEditando({...campanhaEditando, segmentacao: { empresas_ids: [] }});
                      }}
                    >
                      <i className="fas fa-building me-1"></i> Por Empresas
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${modoSegmentacao === 'usuarios' ? 'active' : ''}`}
                      onClick={() => {
                        setModoSegmentacao('usuarios');
                        setCampanhaEditando({...campanhaEditando, segmentacao: { usuarios_ids: [] }});
                      }}
                    >
                      <i className="fas fa-user me-1"></i> Por Usuários
                    </button>
                  </li>
                </ul>

                {/* Segmentação por filtros */}
                {modoSegmentacao === 'filtros' && (
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Tipo de Empresa</label>
                      <select
                        className="form-select"
                        multiple
                        style={{ height: '120px' }}
                        value={campanhaEditando.segmentacao.tipo_empresa || []}
                        onChange={(e) => {
                          const valores = Array.from(e.target.selectedOptions, opt => opt.value);
                          const novaSegmentacao = {...campanhaEditando.segmentacao, tipo_empresa: valores.length > 0 ? valores : undefined, todos: valores.length === 0};
                          setCampanhaEditando({...campanhaEditando, segmentacao: novaSegmentacao});
                          contarDestinatarios(novaSegmentacao);
                        }}
                      >
                        {tiposEmpresa.map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                      <small className="text-muted">Ctrl+Click para múltiplos. Vazio = todos</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Cargo</label>
                      <select
                        className="form-select"
                        multiple
                        style={{ height: '120px' }}
                        value={campanhaEditando.segmentacao.cargo || []}
                        onChange={(e) => {
                          const valores = Array.from(e.target.selectedOptions, opt => opt.value);
                          const novaSegmentacao = {...campanhaEditando.segmentacao, cargo: valores.length > 0 ? valores : undefined};
                          setCampanhaEditando({...campanhaEditando, segmentacao: novaSegmentacao});
                          contarDestinatarios(novaSegmentacao);
                        }}
                      >
                        {cargos.map(cargo => (
                          <option key={cargo} value={cargo}>{cargo}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Segmentação por empresas */}
                {modoSegmentacao === 'empresas' && (
                  <div className="mb-3">
                    <label className="form-label">Selecione as Empresas</label>
                    <select
                      className="form-select"
                      multiple
                      style={{ height: '200px' }}
                      value={(campanhaEditando.segmentacao.empresas_ids || []).map(String)}
                      onChange={(e) => {
                        const valores = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                        const novaSegmentacao = { empresas_ids: valores };
                        setCampanhaEditando({...campanhaEditando, segmentacao: novaSegmentacao});
                        contarDestinatarios(novaSegmentacao);
                      }}
                    >
                      {empresas.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nome} {emp.tipo ? `(${emp.tipo})` : ''}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Ctrl+Click para selecionar múltiplas empresas</small>
                  </div>
                )}

                {/* Segmentação por usuários */}
                {modoSegmentacao === 'usuarios' && (
                  <div className="mb-3">
                    <label className="form-label">Selecione os Usuários</label>
                    <select
                      className="form-select"
                      multiple
                      style={{ height: '200px' }}
                      value={(campanhaEditando.segmentacao.usuarios_ids || []).map(String)}
                      onChange={(e) => {
                        const valores = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                        const novaSegmentacao = { usuarios_ids: valores };
                        setCampanhaEditando({...campanhaEditando, segmentacao: novaSegmentacao});
                        setQuantidadeDestinatarios(valores.length);
                      }}
                    >
                      {usuarios.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nome} ({user.email}) - {user.empresa}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Ctrl+Click para selecionar múltiplos usuários</small>
                  </div>
                )}

                <div className="alert alert-info">
                  <i className="fas fa-users me-2"></i>
                  <strong>{quantidadeDestinatarios}</strong> usuários serão atingidos por esta campanha
                  <button 
                    className="btn btn-sm btn-outline-info ms-3"
                    onClick={() => contarDestinatarios(campanhaEditando.segmentacao)}
                  >
                    Atualizar contagem
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalCampanha(false)}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={salvarCampanha}>
                  <i className="fas fa-save me-2"></i>
                  Salvar Campanha
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Notificação */}
      {modalNotificacao && notificacaoEditando && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-bell me-2"></i>
                  {notificacaoEditando.id ? 'Editar Notificação' : 'Nova Notificação'}
                </h5>
                <button className="btn-close" onClick={() => setModalNotificacao(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-8 mb-3">
                    <label className="form-label">Título *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={notificacaoEditando.titulo}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, titulo: e.target.value})}
                      placeholder="Título da notificação"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Ícone</label>
                    <select
                      className="form-select"
                      value={notificacaoEditando.icone || 'fa-bell'}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, icone: e.target.value})}
                    >
                      <option value="fa-bell">🔔 Sino</option>
                      <option value="fa-info-circle">ℹ️ Info</option>
                      <option value="fa-exclamation-triangle">⚠️ Alerta</option>
                      <option value="fa-check-circle">✅ Sucesso</option>
                      <option value="fa-times-circle">❌ Erro</option>
                      <option value="fa-gift">🎁 Presente</option>
                      <option value="fa-tools">🔧 Manutenção</option>
                      <option value="fa-star">⭐ Estrela</option>
                      <option value="fa-bullhorn">📢 Anúncio</option>
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Mensagem *</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={notificacaoEditando.mensagem}
                    onChange={(e) => setNotificacaoEditando({...notificacaoEditando, mensagem: e.target.value})}
                    placeholder="Mensagem que aparecerá na notificação"
                  />
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Tipo</label>
                    <select
                      className="form-select"
                      value={notificacaoEditando.tipo}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, tipo: e.target.value as any})}
                    >
                      <option value="info">Informação (Azul)</option>
                      <option value="success">Sucesso (Verde)</option>
                      <option value="warning">Aviso (Amarelo)</option>
                      <option value="danger">Importante (Vermelho)</option>
                    </select>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Prioridade</label>
                    <select
                      className="form-select"
                      value={notificacaoEditando.prioridade}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, prioridade: e.target.value as any})}
                    >
                      <option value="baixa">Baixa</option>
                      <option value="normal">Normal</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </select>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Frequência</label>
                    <select
                      className="form-select"
                      value={notificacaoEditando.frequencia}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, frequencia: e.target.value as any})}
                    >
                      <option value="unica">Única vez</option>
                      <option value="diaria">Diária</option>
                      <option value="semanal">Semanal</option>
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Data/Hora Início *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={notificacaoEditando.data_inicio?.slice(0, 16) || ''}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, data_inicio: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Data/Hora Fim (opcional)</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={notificacaoEditando.data_fim?.slice(0, 16) || ''}
                      onChange={(e) => setNotificacaoEditando({...notificacaoEditando, data_fim: e.target.value || undefined})}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Link (opcional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={notificacaoEditando.link || ''}
                    onChange={(e) => setNotificacaoEditando({...notificacaoEditando, link: e.target.value || undefined})}
                    placeholder="/pagina-destino ou https://..."
                  />
                </div>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="notifAtiva"
                    checked={notificacaoEditando.ativa}
                    onChange={(e) => setNotificacaoEditando({...notificacaoEditando, ativa: e.target.checked})}
                  />
                  <label className="form-check-label" htmlFor="notifAtiva">
                    Notificação ativa
                  </label>
                </div>

                <hr />

                <h6><i className="fas fa-users me-2"></i>Segmentação de Destinatários</h6>
                
                {/* Abas de modo de segmentação */}
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${modoSegmentacao === 'filtros' ? 'active' : ''}`}
                      onClick={() => {
                        setModoSegmentacao('filtros');
                        setNotificacaoEditando({...notificacaoEditando, segmentacao: { todos: true }});
                      }}
                    >
                      <i className="fas fa-filter me-1"></i> Por Filtros
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${modoSegmentacao === 'empresas' ? 'active' : ''}`}
                      onClick={() => {
                        setModoSegmentacao('empresas');
                        setNotificacaoEditando({...notificacaoEditando, segmentacao: { empresas_ids: [] }});
                      }}
                    >
                      <i className="fas fa-building me-1"></i> Por Empresas
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${modoSegmentacao === 'usuarios' ? 'active' : ''}`}
                      onClick={() => {
                        setModoSegmentacao('usuarios');
                        setNotificacaoEditando({...notificacaoEditando, segmentacao: { usuarios_ids: [] }});
                      }}
                    >
                      <i className="fas fa-user me-1"></i> Por Usuários
                    </button>
                  </li>
                </ul>

                {/* Segmentação por filtros */}
                {modoSegmentacao === 'filtros' && (
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Tipo de Empresa</label>
                      <select
                        className="form-select"
                        multiple
                        style={{ height: '120px' }}
                        value={notificacaoEditando.segmentacao.tipo_empresa || []}
                        onChange={(e) => {
                          const valores = Array.from(e.target.selectedOptions, opt => opt.value);
                          const novaSegmentacao = {...notificacaoEditando.segmentacao, tipo_empresa: valores.length > 0 ? valores : undefined, todos: valores.length === 0};
                          setNotificacaoEditando({...notificacaoEditando, segmentacao: novaSegmentacao});
                          contarDestinatarios(novaSegmentacao);
                        }}
                      >
                        {tiposEmpresa.map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                      <small className="text-muted">Ctrl+Click para múltiplos. Vazio = todos</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Cargo</label>
                      <select
                        className="form-select"
                        multiple
                        style={{ height: '120px' }}
                        value={notificacaoEditando.segmentacao.cargo || []}
                        onChange={(e) => {
                          const valores = Array.from(e.target.selectedOptions, opt => opt.value);
                          const novaSegmentacao = {...notificacaoEditando.segmentacao, cargo: valores.length > 0 ? valores : undefined};
                          setNotificacaoEditando({...notificacaoEditando, segmentacao: novaSegmentacao});
                          contarDestinatarios(novaSegmentacao);
                        }}
                      >
                        {cargos.map(cargo => (
                          <option key={cargo} value={cargo}>{cargo}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Segmentação por empresas */}
                {modoSegmentacao === 'empresas' && (
                  <div className="mb-3">
                    <label className="form-label">Selecione as Empresas</label>
                    <select
                      className="form-select"
                      multiple
                      style={{ height: '200px' }}
                      value={(notificacaoEditando.segmentacao.empresas_ids || []).map(String)}
                      onChange={(e) => {
                        const valores = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                        const novaSegmentacao = { empresas_ids: valores };
                        setNotificacaoEditando({...notificacaoEditando, segmentacao: novaSegmentacao});
                        contarDestinatarios(novaSegmentacao);
                      }}
                    >
                      {empresas.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nome} {emp.tipo ? `(${emp.tipo})` : ''}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Ctrl+Click para selecionar múltiplas empresas</small>
                  </div>
                )}

                {/* Segmentação por usuários */}
                {modoSegmentacao === 'usuarios' && (
                  <div className="mb-3">
                    <label className="form-label">Selecione os Usuários</label>
                    <select
                      className="form-select"
                      multiple
                      style={{ height: '200px' }}
                      value={(notificacaoEditando.segmentacao.usuarios_ids || []).map(String)}
                      onChange={(e) => {
                        const valores = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                        const novaSegmentacao = { usuarios_ids: valores };
                        setNotificacaoEditando({...notificacaoEditando, segmentacao: novaSegmentacao});
                        setQuantidadeDestinatarios(valores.length);
                      }}
                    >
                      {usuarios.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nome} ({user.email}) - {user.empresa}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Ctrl+Click para selecionar múltiplos usuários</small>
                  </div>
                )}

                <div className="alert alert-info">
                  <i className="fas fa-users me-2"></i>
                  <strong>{quantidadeDestinatarios}</strong> usuários receberão esta notificação
                  <button 
                    className="btn btn-sm btn-outline-info ms-3"
                    onClick={() => contarDestinatarios(notificacaoEditando.segmentacao)}
                  >
                    Atualizar contagem
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalNotificacao(false)}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={salvarNotificacao}>
                  <i className="fas fa-save me-2"></i>
                  Salvar Notificação
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminComunicacao;
